import { memo, useMemo } from 'react';
import { TFunction } from 'i18next';

import { useTranslation } from '@lyearn/i18n';
import { LargeDirectReportees, LargeIndirectReportees } from '@lyearn/icons';
import VisibilitySettingsFormField from '@/components/Form/components/VisibilitySettings';
import { VisibilitySettingsFormFieldProps } from '@/components/Form/components/VisibilitySettings/types';
import { VisibilitySettingsInput, VisibilitySettingsRestrictionTypeEnum } from '@/types/schema';

interface Props {
  contextualUserId: string;
  value: VisibilitySettingsInput;
  onChange: (value: VisibilitySettingsInput) => void;
}

const getVisibilitySettingsContainerOverrides: (
  t: TFunction,
) => VisibilitySettingsFormFieldProps['overrides'] = (t) => ({
  restrictedContent: {
    overrides: {
      label: { variant: 'label-02', colors: 'text-text-primary' },
      addButton: { className: 'mt-6' },
    },
    actionLabel: t('Events:Editor.VisibilitySettings.AddUsers'),
  },
  selectedList: { className: 'mt-6' },
  modal: {
    header: {
      title: t('Events:Editor.VisibilitySettings.AddUsers'),
      description: t('Events:Editor.VisibilitySettings.AddUsersDescription'),
    },
    action: { text: t('Events:Editor.VisibilitySettings.AddUsers') },
  },
});

const EventEditorVisibilitySettings = (props: Props) => {
  const { t } = useTranslation();
  const { contextualUserId, value, onChange } = props;

  const contextualGroupsConfig: VisibilitySettingsFormFieldProps['contextualGroupsConfig'] =
    useMemo(
      () => ({
        supportedContextualGroups: [
          {
            id: VisibilitySettingsRestrictionTypeEnum.Reportees,
            label: t('Events:Editor.MyDirectReportees'),
            Icon: LargeDirectReportees,
          },
          {
            id: VisibilitySettingsRestrictionTypeEnum.Team,
            label: t('Events:Editor.MyIndirectReportees'),
            Icon: LargeIndirectReportees,
          },
        ],
        contextualUserId,
      }),
      [contextualUserId, t],
    );

  const overrides = useMemo(() => getVisibilitySettingsContainerOverrides(t), [t]);

  return (
    <VisibilitySettingsFormField
      contextualGroupsConfig={contextualGroupsConfig}
      overrides={overrides}
      type="users_and_groups"
      value={value}
      onChange={onChange}
    />
  );
};

export default memo(EventEditorVisibilitySettings);
